import React, { useCallback, useEffect, useRef, useState } from "react";
import { FormHelperText, TextField, Typography } from "@mui/material";
import Divider from "@mui/material/Divider";
import DocumentUpload from "@components/FileUpload/FileUpload";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import { useUploadStore, type UploadEntry } from "@components/FileUpload/uploadStore";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import { useTranslation } from "react-i18next";
import { getUploadEntryName } from "../attachmentUtils";
import { preApproveSchema } from "./preApproveSchema";

// This form does not have a store defined, so all fields will be managed via local state or props
// => use regular input components and manage their state locally or via props
interface PreApproveVesselCallDrawerProps {
  isOpen: boolean;
  onClose: (drawerName: string) => void;
  onDocumentUpload?: (file: File) => void;
  onAdditionalDocumentUpload?: (file: File) => void;
  onSubmit?: (payload?: any) => void;
  drawerName?: string;
  titleKey?: string;
  subtitleKey?: string;
  submitLabelKey?: string;
  externalNotesLabelKey?: string;
  externalAttachmentsLabelKey?: string;
  submitStatus?: string;
}

const PreApproveVesselCallDrawer: React.FC<PreApproveVesselCallDrawerProps> = ({
  isOpen,
  onClose,
  onDocumentUpload,
  onAdditionalDocumentUpload,
  onSubmit,
  drawerName = "preApproveDrawer",
  titleKey = "vessel-call.pre-approve.title",
  subtitleKey = "vessel-call.pre-approve.subtitle",
  submitLabelKey = "vessel-call.pre-approve.pre-approve-button",
  externalNotesLabelKey = "vessel-call.common.external-notes",
  externalAttachmentsLabelKey = "vessel-call.common.external-attachments",
  submitStatus = "APPROVED",
}) => {
  const { t } = useTranslation();
  const LANG_BASE = "vessel-call";
  const EMPTY_UPLOAD_ENTRIES: UploadEntry[] = [];
  const charCounterSx = {
    ml: 0,
    mt: 0.5,
    fontSize: 12,
    textAlign: "right",
    color: "text.secondary",
  };
  const TEXTAREA_MAX_CHARS = 1000;
  const externalUploadSignatureRef = useRef<string | null>(null);
  const internalUploadSignatureRef = useRef<string | null>(null);
  const [externalNotes, setExternalNotes] = useState("");
  const [internalNotes, setInternalNotes] = useState("");
  const isPrintableKey = (e: React.KeyboardEvent) =>
    e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;

  const externalOnlySchema = preApproveSchema(t).pick({ externalNotes: true });
  const internalOnlySchema = preApproveSchema(t).pick({ internalNotes: true });
  const [errors, setErrors] = useState<{
    externalNotes?: string;
    internalNotes?: string;
    externalFile?: string;
    internalFile?: string;
  }>({});
  const fileProfiles = useFileUploadProfiles();
  const externalProfile = fileProfiles.sideDrawerApprovalFile;
  const internalProfile = externalProfile;

  const externalFiles = useUploadStore(
    (s) => s.filesByKey.preApproveExternalAttachment ?? EMPTY_UPLOAD_ENTRIES,
  );
  const internalFiles = useUploadStore(
    (s) => s.filesByKey.preApproveInternalAttachment ?? EMPTY_UPLOAD_ENTRIES,
  );
  const setFilesForKey = useUploadStore((s) => s.setFilesForKey);
  const clearFilesForKey = useUploadStore((s) => s.clearFilesForKey);

  const allowedExternalExtensions = externalProfile.format.map((ext) =>
    ext.startsWith(".") ? ext.toLowerCase() : `.${ext.toLowerCase()}`,
  );
  const allowedInternalExtensions = internalProfile.format.map((ext) =>
    ext.startsWith(".") ? ext.toLowerCase() : `.${ext.toLowerCase()}`,
  );

  const clearFilesAndNotes = useCallback(() => {
    removeInternalFile();
    removeExternalFile();
    setExternalNotes("");
    setInternalNotes("");
  }, [clearFilesForKey]);

  useEffect(() => {
    clearFilesAndNotes();
    setErrors({});
  }, [clearFilesAndNotes]);

  const handleClose = () => {
    onClose(drawerName);
  };

  const handleCancel = () => {
    clearFilesAndNotes();
    setErrors({});
    onClose(drawerName);
  };

  const setPreApproveExternalAttachment = useCallback(
    (files: UploadEntry[]) => {
      if (!files.length) {
        clearFilesForKey("preApproveExternalAttachment");
        return;
      }
      setFilesForKey("preApproveExternalAttachment", files);
    },
    [clearFilesForKey, setFilesForKey],
  );
  const setPreApproveInternalAttachment = useCallback(
    (files: UploadEntry[]) => {
      if (!files.length) {
        clearFilesForKey("preApproveInternalAttachment");
        return;
      }
      setFilesForKey("preApproveInternalAttachment", files);
    },
    [clearFilesForKey, setFilesForKey],
  );

  const getFileSignature = (entry: UploadEntry | undefined) => {
    if (!(entry instanceof File)) return null;
    return `${entry.name}-${entry.lastModified}-${entry.size}`;
  };

  const triggerUpload = useCallback(
    (
      entry: UploadEntry | undefined,
      handler: ((file: File) => void) | undefined,
      tracker: React.MutableRefObject<string | null>,
    ) => {
      if (!handler) return;
      if (!(entry instanceof File)) return;

      const signature = getFileSignature(entry);
      if (!signature) return;

      if (tracker.current === signature) return;

      tracker.current = signature;
      handler(entry);
    },
    [],
  );

  const handleFileUpload = ({
    newFiles,
    setUploadErrors,
    setUploadFiles,
    onUploadDocument,
    uploadTracker,
  }: {
    newFiles: File[];
    setUploadErrors: (
      value:
        | { externalFile?: string; internalFile?: string }
        | ((prev: { externalFile?: string; internalFile?: string }) => {
            externalFile?: string;
            internalFile?: string;
          }),
    ) => void;
    setUploadFiles: (files: UploadEntry[]) => void;
    onUploadDocument?: (file: File) => void;
    uploadTracker: React.MutableRefObject<string | null>;
  }) => {
    if (newFiles.length > 1) {
      setUploadErrors({ externalFile: t("upload.one-document") });
      return;
    }

    const file = newFiles[0];
    if (!file) {
      return;
    }

    setUploadErrors((prev) => ({ ...prev, files: undefined }));
    setUploadFiles([file]);
    triggerUpload(file, onUploadDocument, uploadTracker);
  };

  const removeInternalFile = () => {
    clearFilesForKey("preApproveInternalAttachment");
  };
  const removeExternalFile = () => {
    clearFilesForKey("preApproveExternalAttachment");
  };

  const handleSubmit = () => {
    const toValidate = {
      files: undefined,
      internalNotes,
      externalNotes,
    };

    const validation = preApproveSchema(t).safeParse(toValidate);
    if (!validation.success) {
      const flat = validation.error.flatten().fieldErrors;
      setErrors((prev) => ({
        ...prev,
        externalNotes: flat.externalNotes?.[0],
        internalNotes: flat.internalNotes?.[0],
      }));
      return;
    }

    const hasInternalAttachment = internalFiles.length > 0;
    const hasExternalAttachment = externalFiles.length > 0;

    const payload = {
      externalNotes,
      internalNotes,
      hasInternalAttachment,
      hasExternalAttachment,
      externalAttachmentName: hasExternalAttachment
        ? getUploadEntryName(externalFiles[0]) || undefined
        : undefined,
      internalAttachmentName: hasInternalAttachment
        ? getUploadEntryName(internalFiles[0]) || undefined
        : undefined,
      status: submitStatus,
    };
    onSubmit?.(payload);
    clearFilesAndNotes();
  };

  return (
    <div data-testid="pre-approve-vessel-call">
      <SideDrawer
        isOpen={isOpen}
        title={t(titleKey)}
        onClose={handleClose}
        onCancel={handleCancel}
        submitLabel={t(submitLabelKey)}
        onSubmit={handleSubmit}
      >
        <Typography
          variant="body1"
          data-testid="pre-approve-vessel-call-title"
          sx={{ color: "text.primary", px: 3, pb: 3 }}
        >
          {t(subtitleKey)}
        </Typography>
        <div className="flex-1 py-2">
          <div className="flex flex-col gap-4 px-6">
            {/* External Notes */}
            <div className="flex flex-col gap-2 " data-testid="pre-approve-external-notes">
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {t(externalNotesLabelKey)}
              </Typography>
              <TextField
                multiline
                minRows={4}
                value={externalNotes}
                placeholder={t(externalNotesLabelKey)}
                sx={{ "& textarea": { padding: "12px", borderRadius: "8px" } }}
                inputProps={{ maxLength: TEXTAREA_MAX_CHARS }}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (!isPrintableKey(e)) return;
                  if (externalNotes.length >= TEXTAREA_MAX_CHARS) {
                    setErrors((prev) => ({
                      ...prev,
                      externalNotes: t("form-validation.max-length", {
                        label: t(externalNotesLabelKey),
                        max: TEXTAREA_MAX_CHARS,
                      }),
                    }));
                  }
                }}
                onBlur={() => {
                  const res = externalOnlySchema.safeParse({ externalNotes });
                  if (!res.success) {
                    const flat = res.error.flatten().fieldErrors;
                    setErrors((prev) => ({ ...prev, externalNotes: flat.externalNotes?.[0] }));
                  } else {
                    setErrors((prev) => ({ ...prev, externalNotes: undefined }));
                  }
                }}
                onChange={(e) => {
                  const val = e.target.value;
                  setExternalNotes(val);
                  const res = externalOnlySchema.safeParse({ externalNotes: val });
                  if (!res.success) {
                    const flat = res.error.flatten().fieldErrors;
                    setErrors((prev) => ({ ...prev, externalNotes: flat.externalNotes?.[0] }));
                  } else {
                    setErrors((prev) => ({ ...prev, externalNotes: undefined }));
                  }
                }}
              />
              {errors.externalNotes && (
                <FormHelperText sx={{ color: "error.main", m: 0 }}>
                  {errors.externalNotes}
                </FormHelperText>
              )}
              <FormHelperText sx={charCounterSx}>
                {externalNotes.length}/{TEXTAREA_MAX_CHARS}
              </FormHelperText>
              <DocumentUpload
                label={t(externalAttachmentsLabelKey)}
                testId="pre-approve-external-attachment-upload"
                name="preApproveExternalAttachment"
                maxFiles={1}
                allowedExtensions={allowedExternalExtensions}
                onUpload={(newFiles) =>
                  handleFileUpload({
                    newFiles,
                    setUploadErrors: setErrors,
                    setUploadFiles: setPreApproveExternalAttachment,
                    onUploadDocument: onDocumentUpload,
                    uploadTracker: externalUploadSignatureRef,
                  })
                }
                conditions={externalProfile}
                files={externalFiles}
                setFiles={(next) => setFilesForKey("preApproveExternalAttachment", next)}
                uploadDescription={t("upload.dragOrDropDescription")}
              />
              {errors.externalFile && (
                <FormHelperText sx={{ color: "error.main", m: 0 }}>
                  {errors.externalFile}
                </FormHelperText>
              )}
              {externalFiles[0] && (
                <SingleFile
                  file={externalFiles[0]}
                  fieldName="preApproveExternalAttachment"
                  setFiles={() => {
                    setErrors((prev) => ({ ...prev, files: undefined }));
                    setPreApproveExternalAttachment([]);
                    externalUploadSignatureRef.current = null;
                  }}
                />
              )}
            </div>
            <Divider />
            {/* Internal Notes */}
            <div className="flex flex-col gap-2" data-testid="pre-approve-internal-notes">
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {t(`${LANG_BASE}.common.internal-notes`)}
              </Typography>
              <TextField
                multiline
                minRows={4}
                value={internalNotes}
                inputProps={{ maxLength: TEXTAREA_MAX_CHARS }}
                placeholder={t(`${LANG_BASE}.common.internal-notes`)}
                sx={{ "& textarea": { padding: "12px", borderRadius: "8px" } }}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (!isPrintableKey(e)) return;
                  if (internalNotes.length >= TEXTAREA_MAX_CHARS) {
                    setErrors((prev) => ({
                      ...prev,
                      internalNotes: t("form-validation.max-length", {
                        label: t(`${LANG_BASE}.common.internal-notes`),
                        max: TEXTAREA_MAX_CHARS,
                      }),
                    }));
                  }
                }}
                onBlur={() => {
                  const res = internalOnlySchema.safeParse({ internalNotes });
                  if (!res.success) {
                    const flat = res.error.flatten().fieldErrors;
                    setErrors((prev) => ({ ...prev, internalNotes: flat.internalNotes?.[0] }));
                  } else {
                    setErrors((prev) => ({ ...prev, internalNotes: undefined }));
                  }
                }}
                onChange={(e) => {
                  const val = e.target.value;
                  setInternalNotes(val);
                  const res = internalOnlySchema.safeParse({ internalNotes: val });
                  if (!res.success) {
                    const flat = res.error.flatten().fieldErrors;
                    setErrors((prev) => ({ ...prev, internalNotes: flat.internalNotes?.[0] }));
                  } else {
                    setErrors((prev) => ({ ...prev, internalNotes: undefined }));
                  }
                }}
              />
              {errors.internalNotes && (
                <FormHelperText sx={{ color: "error.main", m: 0 }}>
                  {errors.internalNotes}
                </FormHelperText>
              )}
              <FormHelperText sx={charCounterSx}>
                {internalNotes.length}/{TEXTAREA_MAX_CHARS}
              </FormHelperText>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {t(`${LANG_BASE}.common.internal-attachments`)}
              </Typography>
              <DocumentUpload
                testId="pre-approve-internal-attachment-upload"
                onUpload={(newFiles) =>
                  handleFileUpload({
                    newFiles,
                    setUploadErrors: setErrors,
                    setUploadFiles: setPreApproveInternalAttachment,
                    onUploadDocument: onAdditionalDocumentUpload,
                    uploadTracker: internalUploadSignatureRef,
                  })
                }
                name="preApproveInternalAttachment"
                conditions={internalProfile}
                files={internalFiles}
                setFiles={(next) => setFilesForKey("preApproveInternalAttachment", next)}
                maxFiles={1}
                allowedExtensions={allowedInternalExtensions}
                uploadDescription={t("upload.dragOrDropDescription")}
              />
              {errors.internalFile && (
                <FormHelperText sx={{ color: "error.main", m: 0 }}>
                  {errors.internalFile}
                </FormHelperText>
              )}
              {internalFiles[0] && (
                <SingleFile
                  file={internalFiles[0]}
                  testId="pre-approve-internal-attachment"
                  fieldName="preApproveInternalAttachment"
                  currentFileIndex={0}
                  setFiles={() => {
                    setErrors((prev) => ({ ...prev, files: undefined }));
                    setPreApproveInternalAttachment([]);
                    internalUploadSignatureRef.current = null;
                  }}
                  removeFn={removeInternalFile}
                />
              )}
            </div>
          </div>
        </div>
      </SideDrawer>
    </div>
  );
};

export default PreApproveVesselCallDrawer;
